import { trackEvent } from "../events";
import { flushBeacon } from "../tracker";
import { isValidSectionKey, isProd } from "../config";

export default {
	props: {
		sectionKey: {
			type: String,
			required: true,
		},
		sectionRef: {
			type: String,
			default: "sectionRoot",
		},
		minDwellMs: {
			type: Number,
			default: 1000,
		},
		viewThreshold: {
			type: Number,
			default: 0.5,
		},
	},
	data() {
		return {
			sectionTracking: {
				observer: null,
				isVisible: false,
				visibleSince: null,
				accumulatedDwellMs: 0,
				hasFiredViewed: false,
				staggerHandle: null,
				staggerMethod: null,
				visibilityChangeHandler: null,
			},
		};
	},
	created() {
		if (!isProd() && !isValidSectionKey(this.sectionKey)) {
			console.warn(
				`[tracking] sectionTrackingMixin: "${this.sectionKey}" is not a recognized DASHBOARD_SECTIONS key`
			);
		}
	},
	mounted() {
		this._setupSectionTracking();
	},
	beforeDestroy() {
		this._teardownSectionTracking();
	},
	methods: {
		_setupSectionTracking() {
			this._teardownSectionTracking();

			const state = this.sectionTracking;

			if (typeof window.requestIdleCallback === "function") {
				state.staggerMethod = "idle";
				state.staggerHandle = window.requestIdleCallback(() => {
					this._createObserver();
				});
			} else {
				state.staggerMethod = "timeout";
				state.staggerHandle = setTimeout(() => {
					this._createObserver();
				}, 0);
			}

			state.visibilityChangeHandler = () => {
				if (document.visibilityState === "hidden") {
					if (state.isVisible) {
						this._accumulateDwell();
						this._maybeFlushDwell();
						// The tracker's own hidden flush already ran, so send the dwell now.
						flushBeacon();
					}
					state.visibleSince = null;
				} else if (state.isVisible) {
					state.visibleSince = performance.now();
				}
			};
			document.addEventListener("visibilitychange", state.visibilityChangeHandler);
		},
		_teardownSectionTracking() {
			const state = this.sectionTracking;

			if (state.isVisible) {
				this._accumulateDwell();
				this._maybeFlushDwell();
			}

			this._cancelStagger();
			this._teardownObserver();

			if (state.visibilityChangeHandler) {
				document.removeEventListener("visibilitychange", state.visibilityChangeHandler);
				state.visibilityChangeHandler = null;
			}

			state.isVisible = false;
			state.visibleSince = null;
			state.accumulatedDwellMs = 0;
			state.hasFiredViewed = false;
		},
		_cancelStagger() {
			const state = this.sectionTracking;
			if (state.staggerHandle == null) return;

			if (state.staggerMethod === "idle" && typeof window.cancelIdleCallback === "function") {
				window.cancelIdleCallback(state.staggerHandle);
			} else {
				clearTimeout(state.staggerHandle);
			}
			state.staggerHandle = null;
			state.staggerMethod = null;
		},
		_createObserver() {
			const raw = this.$refs[this.sectionRef];
			// If the ref is a component, use its $el.
			const el = raw && raw.$el ? raw.$el : raw;
			if (!el) return;

			this.sectionTracking.observer = new IntersectionObserver(
				(entries) => {
					entries.forEach((entry) => this._handleIntersection(entry));
				},
				{ threshold: this.viewThreshold }
			);
			this.sectionTracking.observer.observe(el);
		},
		_teardownObserver() {
			const state = this.sectionTracking;
			if (state.observer) {
				state.observer.disconnect();
				state.observer = null;
			}
		},
		_handleIntersection(entry) {
			const state = this.sectionTracking;
			if (entry.isIntersecting) {
				if (!state.isVisible) {
					state.isVisible = true;
					state.visibleSince = performance.now();
				}
				if (!state.hasFiredViewed) {
					trackEvent("section_viewed", { sectionId: this.sectionKey });
					state.hasFiredViewed = true;
				}
			} else if (state.isVisible) {
				this._accumulateDwell();
				state.isVisible = false;
				this._maybeFlushDwell();
			}
		},
		_accumulateDwell() {
			const state = this.sectionTracking;
			if (state.visibleSince != null) {
				state.accumulatedDwellMs += performance.now() - state.visibleSince;
				state.visibleSince = null;
			}
		},
		_maybeFlushDwell() {
			const state = this.sectionTracking;
			if (state.accumulatedDwellMs >= this.minDwellMs) {
				trackEvent("section_dwell", {
					sectionId: this.sectionKey,
					dwellMs: Math.round(state.accumulatedDwellMs),
				});
			}
			state.accumulatedDwellMs = 0;
		},
	},
};
